/*
 * Booking from My Trades (owner, 2026-11-02, vault project-hub-booking): Get a quote prices on the quoter and goes straight
 * to booking, with a plan (every 2 weeks) picked first so a one time clean does not take the place of a schedule.
 *
 * - A client who had a standard, deep, move in or move out clean in the last PLAN_READY_DAYS days starts a plan at the plan
 *   price: the home is already clean, so no first visit price on top.
 * - After a standard, deep or move in clean, the hub offers "Keep it this clean". Not after a move out (they are leaving),
 *   not after post construction, Airbnb or other non-home cleans, not to businesses, not to a client with a schedule (or
 *   one stopped in the last PLAN_RECENT_DAYS), and not once PLAN_READY_DAYS have passed.
 * - A second one time booking within ONE_TIME_NUDGE_DAYS shows the plan price first on the quote page.
 */
import { z } from "zod";
import { homeAnswersShape } from "./website.zod.js";
import { hourlyAnswersShape } from "./prices.js";

export const PLAN_READY_DAYS = 30;
export const ONE_TIME_NUDGE_DAYS = 90;
/** A schedule paused or stopped within this many days still counts: the office talks to them, not the card. */
export const PLAN_RECENT_DAYS = 60;
/** The schedule picked first in the hub quoter. */
export const HUB_DEFAULT_OFTEN = "biweekly";

const MOVE_OUT = /\bmove[\s-]*out\b|\bmoving[\s-]*out\b/i;
const READY = /\b(standard|regular|house|home|deep|move[\s-]*(in|out)|moving)\b/i;
/**
 * Cleans that are not a lived-in home, even with "deep" or "home" in the name (owner, 2026-11-02): post construction and
 * remodels, short term rentals (Airbnb, VRBO, vacation homes, turnovers), offices and businesses, events, and single tasks.
 */
const NOT_A_HOME =
  /construction|renovation|remodel|air\s*bnb|vrbo|short[\s-]*term|vacation|turnover|rental|office|commercial|business|event|party|window|carpet|upholstery|pressure/i;

/** A done visit of this job readies the home for a plan (standard, deep, move in or out of a lived-in home). */
export function readiesForPlan(job: string): boolean {
  return READY.test(job) && !NOT_A_HOME.test(job);
}

/** After a done visit of this job the hub offers a plan: standard, deep or move in, never move out. */
export function offersPlanAfter(job: string): boolean {
  return readiesForPlan(job) && !MOVE_OUT.test(job);
}

/** What the hub quoter prices: a home from the price list, or maid service by the hour. */
export const hubAnswers = z.union([homeAnswersShape, hourlyAnswersShape]);
export type HubAnswers = z.infer<typeof hubAnswers>;

export const hubPriceBody = z.object({ companyId: z.uuid(), answers: hubAnswers }).strict();

/** The hub quoter's setup for one brand: the clean list, the home as last quoted, and what the client qualifies for. */
export const hubQuoterAnswer = z.object({
  services: z.array(z.object({ id: z.string(), label: z.string(), repeats: z.boolean() })),
  extras: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      cents: z.number(),
      /** The flat price on a repeating plan, when it has one. */
      planCents: z.number().nullable().default(null),
      each: z.boolean(),
      only: z.string().nullable(),
    }),
  ),
  /** Schedules the price list offers (weekly, biweekly, monthly), for cleans that repeat. */
  repeats: z.array(z.object({ id: z.string(), label: z.string() })),
  hourlyCents: z.number(),
  /** Their home as last quoted, else a 2 bed 1 bath start. */
  home: homeAnswersShape,
  /** The plain repeating clean ("Standard cleaning"), for Start a regular schedule. */
  regularId: z.string().nullable(),
  /** A standard, deep or move clean in the last 30 days: a plan starts at the plan price. */
  planReady: z.boolean(),
  /** One time cleans booked in the last 90 days: one or more shows the plan price first. */
  oneTimes: z.number(),
});
export type HubQuoterAnswer = z.infer<typeof hubQuoterAnswer>;

export const hubPriceAnswer = z.object({
  /** null when the list has no price for these answers. */
  price: z
    .object({
      title: z.string(),
      firstVisitCents: z.number(),
      /** Every visit after the first, on a schedule. */
      repeat: z.object({ label: z.string(), perVisitCents: z.number() }).nullable(),
      /** The first visit is a plan visit (planReady). */
      planStart: z.boolean(),
    })
    .nullable(),
});
export type HubPriceAnswer = z.infer<typeof hubPriceAnswer>;

/** Book it: the quote page to pick the day on. */
export const hubQuoteAnswer = z.object({ link: z.string() });
